import { Text } from "@assetwatch/ui";
import type { ReactNode } from "react";

export type MetricHighlightValueTone = "positive" | "negative";

export type MetricHighlightItem = {
  id: string;
  label: string;
  value: string;
  /** Renders before the value (e.g. icon) inside the value row */
  valueAdornmentStart?: ReactNode;
  /** Renders after the label (e.g. help tooltip trigger) */
  labelEnd?: ReactNode;
  /** Secondary line under the value (e.g. supporting stat) */
  detail?: string;
  /** Default positive (green); negative uses destructive red */
  valueTone?: MetricHighlightValueTone;
  testId?: string;
};

type Props = {
  items: MetricHighlightItem[];
  /** Overrides default aria-label on the deck container */
  groupAriaLabel?: string;
};

export type MetricHighlightCustomCell = {
  id: string;
  label: string;
  labelEnd?: ReactNode;
  body: ReactNode;
};

type CustomDeckProps = {
  cells: MetricHighlightCustomCell[];
  groupAriaLabel?: string;
  /** Uppercase section label vs default body label */
  labelVariant?: "default" | "eyebrow";
};

const deckClassName =
  "grid w-full grid-cols-1 overflow-hidden rounded-(--radius-none) border border-outline-subtle bg-surface md:grid-cols-3";
const cellClassName =
  "flex min-w-0 flex-col gap-(--spacing-8) p-(--spacing-20) md:px-(--spacing-24) [&:not(:first-child)]:border-t [&:not(:first-child)]:border-outline-subtle md:[&:not(:first-child)]:border-l md:[&:not(:first-child)]:border-t-0";
const labelRowClassName = "flex flex-wrap items-center gap-(--spacing-6)";
const labelClassName = "m-0";
const labelEndClassName = "inline-flex items-center leading-none";

export function MetricHighlightCustomDeck({
  cells,
  labelVariant = "default",
}: CustomDeckProps) {
  return (
    <div className={deckClassName}>
      {cells.map((cell) => {
        const labelId = `metric-custom-${cell.id}-label`;
        return (
          <div key={cell.id} className={cellClassName}>
            <div className={labelRowClassName}>
              {labelVariant === "eyebrow" ? (
                <div
                  id={labelId}
                  className="m-0 text-(length:--font-size-body-md) font-semibold uppercase text-text-subtle"
                >
                  {cell.label}
                </div>
              ) : (
                <div className={labelClassName}>
                  <Text as="div" id={labelId} size="sm">
                    {cell.label}
                  </Text>
                </div>
              )}
              {cell.labelEnd ? (
                <span className={labelEndClassName}>{cell.labelEnd}</span>
              ) : null}
            </div>
            <div className="mt-(--spacing-4) flex min-w-0 flex-1 flex-col gap-(--spacing-10) text-text-default">
              {cell.body}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MetricHighlightDeck({ items }: Props) {
  return (
    <div className={deckClassName}>
      {items.map((item) => {
        const labelId = `metric-highlight-${item.id}-label`;
        const isNegative = item.valueTone === "negative";
        return (
          <div
            key={item.id}
            className={cellClassName}
            data-testid={item.testId}
          >
            <div className={labelRowClassName}>
              <div className={labelClassName}>
                <Text as="div" id={labelId} size="sm">
                  {item.label}
                </Text>
              </div>
              {item.labelEnd ? (
                <span className={labelEndClassName}>{item.labelEnd}</span>
              ) : null}
            </div>
            <p
              className={`m-0 break-words text-(length:--font-size-headline-sm) font-bold leading-(--line-height-28) text-positive md:text-(length:--font-size-headline-lg) md:leading-(--line-height-36) ${
                isNegative ? "text-negative" : ""
              }`}
            >
              {item.valueAdornmentStart ? (
                <span className="flex flex-wrap items-center gap-(--spacing-12)">
                  <span
                    className={`inline-flex shrink-0 items-center justify-center text-(length:--font-size-title-xl) leading-none text-primary ${
                      isNegative ? "text-inherit" : ""
                    }`}
                    aria-hidden
                  >
                    {item.valueAdornmentStart}
                  </span>
                  <span>{item.value}</span>
                </span>
              ) : (
                item.value
              )}
            </p>
            {item.detail ? (
              <div className="m-0 mt-(--spacing-2)">
                <Text as="div" size="sm" tone="subtle">
                  {item.detail}
                </Text>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
